/**
 * Mobile Time Picker Effects Hook
 * Extracted from CreateTransactionMobile.tsx
 * 
 * Handles time picker initialization and edit mode time management
 */

import { useEffect } from "react";
import { TransactionCustomFieldValues } from "../../../../types";

// Local types matching CreateTransactionMobile.tsx
interface FormData {
  category: string;
  amount: string;
  currency: string;
  rate: string;
  defaultCurrencyRate: string;
  date: string;
  time: string;
  accountId: string;
  accountGroup: string;
  details?: any;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

interface TransferFormData {
  fromAccountId: string;
  toAccountId: string;
  amount: string;
  date: string;
  time: string;
  transferRate: string;
  feeAmount?: string;
  feeAccountId?: string;
  customFieldValues?: TransactionCustomFieldValues;
  customFieldPendingFiles?: Record<string, File>;
}

export type FocusedField = "amount" | "date" | "time" | "account" | "currency" | "category" | "fromAccount" | "toAccount" | "rate" | "defaultCurrencyRate" | "feeAccount" | "feeAmount" | `detail:${string}` | null;

export interface UseMobileTimePickerEffectsProps {
  transactionType: "expense" | "income" | "transfer";
  isEditMode: boolean;
  editingTransaction: any | null;
  focusedField: FocusedField;
  formData: FormData;
  setFormData: React.Dispatch<React.SetStateAction<FormData>>;
  transferFormData: TransferFormData;
  setTransferFormData: React.Dispatch<React.SetStateAction<TransferFormData>>;
  timeWasEnabled: boolean;
  getCurrentTime: () => string;
  setCenteredHour: React.Dispatch<React.SetStateAction<string>>;
  setCenteredMinute: React.Dispatch<React.SetStateAction<string>>;
}

export function useMobileTimePickerEffects({
  transactionType,
  isEditMode,
  editingTransaction,
  focusedField,
  formData,
  setFormData,
  transferFormData,
  setTransferFormData,
  timeWasEnabled,
  getCurrentTime,
  setCenteredHour,
  setCenteredMinute,
}: UseMobileTimePickerEffectsProps) {
  // Ensure time is always set in state when time picker opens
  // In edit mode, set current time if user explicitly opened time picker (time was cleared)
  useEffect(() => {
    // In edit mode, wait for transaction to load before doing anything with time
    if (isEditMode && !editingTransaction) {
      return; // Don't run until transaction is loaded
    }
    
    if (focusedField === "time") {
      const currentTime = transactionType === "transfer" ? transferFormData.time : formData.time;
      // Check if time is empty string or undefined/null
      if (!currentTime || currentTime === "") {
        // Set current time when time picker opens (both create and edit mode)
        // In edit mode, if time is empty, it means user cleared it and wants to add new time
        const newTime = getCurrentTime();
        if (transactionType === "transfer") {
          setTransferFormData((prev) => ({ ...prev, time: newTime }));
        } else {
          setFormData((prev) => ({ ...prev, time: newTime }));
        }
      } else {
        // Initialize centered values with existing time (use the actual time, not current time)
        const [hour, minute] = currentTime.split(":");
        setCenteredHour(hour || "");
        setCenteredMinute(minute || "");
      }
    }
    
    // In edit mode, if time wasn't enabled and time field is not focused, ensure time stays empty
    // BUT: If user is currently interacting with time picker (focusedField === "time"), don't clear it
    // IMPORTANT: Only run this after transaction is loaded (editingTransaction is set)
    // This prevents race conditions where useEffect runs before transaction data is loaded
    
    // This allows user to add time when editing transactions that didn't originally have time
    const isTimeFieldFocused = focusedField === "time";
    if (isEditMode && !timeWasEnabled && !isTimeFieldFocused) {
      if (transactionType === "transfer" && transferFormData.time && transferFormData.time.trim() !== "") {
        setTransferFormData((prev) => ({ ...prev, time: "" }));
      } else if (transactionType !== "transfer" && formData.time && formData.time.trim() !== "") {
        setFormData((prev) => ({ ...prev, time: "" }));
      }
    }
  }, [focusedField, transactionType, isEditMode, timeWasEnabled, transferFormData.time, formData.time, editingTransaction, getCurrentTime, setFormData, setTransferFormData, setCenteredHour, setCenteredMinute]);

  // Additional safeguard: In edit mode, if time wasn't enabled, ensure time is always empty
  // BUT: Don't clear if user is currently adding time (focusedField === "time")
  // IMPORTANT: Only run this after transaction is loaded (editingTransaction is set)
  // This prevents race conditions where useEffect runs before transaction data is loaded
  useEffect(() => {
    // Don't run until transaction is loaded in edit mode
    if (isEditMode && !editingTransaction) {
      return;
    }
    
    if (isEditMode && !timeWasEnabled && focusedField !== "time") {
      if (transactionType === "transfer") {
        if (transferFormData.time && transferFormData.time.trim() !== "") {
          setTransferFormData((prev) => ({ ...prev, time: "" }));
        }
      } else {
        if (formData.time && formData.time.trim() !== "") {
          setFormData((prev) => ({ ...prev, time: "" }));
        }
      }
    }
  }, [isEditMode, timeWasEnabled, transactionType, focusedField, transferFormData.time, formData.time, editingTransaction, setFormData, setTransferFormData]);
}
